import { TFunction, useTranslation } from 'react-i18next';
import {
  ApiButton,
  Box,
  Button,
  ContentTemplate,
  CustomSelect,
  MUIDataTable,
  MUIDataTableColumn,
  MUIDataTableColumnDef,
  SearchBar,
  SectionHeader,
  TABLE_INITIAL_STATE,
  TableActionTypes,
  TableState,
  Typography,
  useDataTable,
  useSnackbarHandler,
  Theme,
} from 'web-library';
import React, { useEffect, useState } from 'react';
import { MutationStatus, UseMutateFunction, useQueryClient } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';
import IconWithLabel from 'web-library/stories/atom/IconWithLabel/IconWithLabel';
import { Theme as AugmentedTheme, useTheme, SelectChangeEvent } from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import { AxiosError, AxiosResponse } from 'axios';
import Icon, { IconName } from 'web-library/stories/atom/Icon/Icon';

import { ChoosePaperSubjectModal } from '../PaperSubjectModal';
import {
  useGetSelectedSubjects,
  useGetSubjectDetail,
  usePostExamSubjectRegistrationDraft,
  usePostExamSubjects,
} from '../../api/candidate/exam/@candidate/queries-services/queries';
import {
  ExamSaveSubjectRegistrationDraftRequest,
  ExamSubjects,
  ViewSelectedSubjectsResponseBody,
} from '../../api/candidate/exam/model';
import SelectedSubjectsDrawer from './SelectedSubjectsDrawer';
import {
  GET_EXAM_REGISTRATION,
  GET_SELECTED_SUBJECTS,
} from '../../api/candidate/exam/@candidate/queries-services/keys';
import { candidateService, GenericErrorResponse } from '../../api';
import ViewSubjectDetailsModal from '../ViewSubjectDetailsModal';
import { dropDownMappingToSelect, useAPIDropdown } from '../../hooks/dropdown';
import { customFilterChipRender } from '../../hooks/table';
import { getIndexValue } from '../../utils/ArrayUtils';
import SelectAddSubjectsNavButtons, { INavigation } from './SelectAddSubjectsNavButtons';
import { setSubjectDetails } from '../../utils/StringUtils';

const useStyles = makeStyles()((theme) =>
({
  textContainer: {
    marginBottom: theme.spacing(3),
  },
  subjectContainer: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  subjectText: {
    width: '100%',
  },
  actionButton: {
    padding: 0,
    whiteSpace: 'nowrap',
    '& span': {
      justifyContent: 'flex-start',
      '& h5': {
        width: 'max-content',
      },
    },
  },
  editContainer: {
    marginBottom: theme.spacing(3),
  },
  deleteButton: {
    color: theme.color.icon.iconTrash,
  },
  deleteContainer: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(6),
  },
  searchContainer: {
    paddingBottom: theme.spacing(2),
  },
  successAdded: {
    color: `${theme.color.state.stateSuccess} !important`,
    padding: 0,
    '& span': {
      justifyContent: 'flex-start',
    },
  },
  searchInput: {
    // maxWidth: '100%',
  },
  viewDetailsButton: {
    padding: `${theme.spacing(0)} !important`,
    justifyContent: 'left',
    textAlign: 'left',
    whiteSpace: 'nowrap',
  },
  select: {
    marginBottom: theme.spacing(3),
  },
}));

interface ISelectSubjectsHooks {
  history: any;
  t: TFunction<'translation'>;
  mutatePostExamSubjectRegistrationDraft: UseMutateFunction<
    AxiosResponse,
    AxiosError<AxiosError<GenericErrorResponse>>,
    ExamSaveSubjectRegistrationDraftRequest
  >;
  mutatePostExamSubjectRegistrationDraftStatus: MutationStatus;
  applicationUuid: string;
  tableDispatch: React.Dispatch<TableActionTypes>;
}

interface ISelectSubjectsStyling {
  classes: Record<string,string>;
  theme: Theme;
}

interface ISelectSubjectsHandleModal {
  onChoosePaperModalOpenHandler: (code: string) => void;
  onChoosePaperModalCloseHandler: (code: string) => void;
  onViewDetailOpenHandler: (code: string) => void;
}

interface ITableColumns {
  data: ISelectSubjectsData;
  styling: ISelectSubjectsStyling;
  hooks: ISelectSubjectsHooks;
  handleModal: ISelectSubjectsHandleModal;
}

interface ISelectSubjectsData {
  subjectListData: ExamSubjects[];
  selectedSubjectsData: ViewSelectedSubjectsResponseBody[] | [];
  examSeries: { functionCode: any; typeMapping: { [key: string]: any } };
  languages: { functionCode: any; typeMapping: { [key: string]: any } };
  examLevels: { functionCode: any; typeMapping: { [key: string]: any } };
  state: TableState;
}

const examLevelColumn = (
  data: ISelectSubjectsData,
  styling: ISelectSubjectsStyling,
  hooks: ISelectSubjectsHooks
): MUIDataTableColumnDef => {
  const {
    examLevels: { functionCode: filterCodeExamLevel, typeMapping: filterMappingExamLevel },
    state,
  } = data;
  const { t, tableDispatch } = hooks;
  const { classes } = styling;

  return {
    name: 'examLevel',
    label: t('exam->>SelectSubjects.ExamLevel'),
    options: {
      sort: true,
      searchable: true,
      filter: true,
      filterType: 'custom',
      setCellProps: () => ({ style: { width: '16%' } }),
      customFilterListOptions: {
        render: (v: string[]) => [filterMappingExamLevel[v[0]].children],
        update: (filterList: string[][],
          filterPos: number,
          index: number) => {
          // update table filter state
          tableDispatch({
            type: 'UPDATE_FILTERS',
            payload: { ...state.filters, examLevel: [] },
          });

          // update table inner filter
          return customFilterChipRender(filterList, filterPos, index);
        },
      },
      filterOptions: {
        fullWidth: true,
        display: (filterList: string[][], onChange: (val: string | string[], index: number, column: MUIDataTableColumn) => void, index: number, column: MUIDataTableColumn) => (
          <CustomSelect
            className={classes.select}
            // eslint-disable-next-line react/destructuring-assignment
            value={filterList[index][0]}
            displayEmpty
            menuItems={filterCodeExamLevel}
            onChange={(event) => {
              // eslint-disable-next-line no-param-reassign
              filterList[index][0] = event.target.value as string;
              onChange(filterList[index], index, column);
            }}
            label={t('exam->>SelectSubjects.ExamLevel')}
            labelId="inputSelectLabel-examLevel"
            id="inputSelect-examLevel"
          />
        ),
      },
    },
  };
};

const examSeriesColumn = (
  data: ISelectSubjectsData,
  styling: ISelectSubjectsStyling,
  hooks: ISelectSubjectsHooks,
  filterOnChange: (
    filterList: string[][],
    onChange: (val: string | string[], index: number, column: MUIDataTableColumn) => void,
    index: number,
    column: MUIDataTableColumn,
    event: SelectChangeEvent<unknown>
  ) => void
): MUIDataTableColumnDef => {
  const {
    examSeries: { functionCode: filterCodeExamSeries, typeMapping: filterMappingExamSeries },
    state,
  } = data;
  const { t, tableDispatch } = hooks;
  const { classes } = styling;

  return {
    name: 'examSeries',
    label: t('exam->>SelectSubjects.ExamSeries'),
    options: {
      sort: true,
      searchable: true,
      filter: true,
      filterType: 'custom',
      setCellProps: () => ({ style: { width: '15%' } }),
      customFilterListOptions: {
        render: (v: any) => {
          if (v.length > 1) {
            return [`${filterMappingExamSeries[v[0]].children}, ..`];
          }
          if (v.length === 1) {
            return [filterMappingExamSeries[v[0]].children];
          }
          return [];
        },
        update: (filterList: string[][],
          filterPos: number,
          index: number) => {
          // update table filter state
          tableDispatch({
            type: 'UPDATE_FILTERS',
            payload: { ...state.filters, examSeries: [] },
          });

          // update table inner filter
          return customFilterChipRender(filterList, filterPos, index);
        },
      },
      filterOptions: {
        fullWidth: true,
        display: (filterList: string[][], onChange: (val: string | string[], index: number, column: MUIDataTableColumn) => void, index: number, column: MUIDataTableColumn) => (
          <CustomSelect
            className={classes.select}
            displayEmpty
            variant="filled"
            multiple
            // eslint-disable-next-line no-param-reassign,react/destructuring-assignment
            value={filterList[index]}
            menuItems={filterCodeExamSeries}
            onChange={(e: SelectChangeEvent<unknown>) => {
              filterOnChange(filterList, onChange, index, column, e);
            }}
            label={t('exam->>SelectSubjects.ExamSeries')}
            labelId="inputSelectLabel-examSeries"
            id="inputSelect-examSeries"
            renderValue={(selected: unknown | string[]) =>
              Array.isArray(selected) && selected.length > 0
                ? // @ts-ignore
                filterCodeExamSeries
                  .filter((examSeries: any) => selected.includes(examSeries.value))
                  .map((s: any) => s.children)
                  .join(', ')
                : 'Select Exam Series'
            }
          />
        ),
      },
    },
  };
};

const languageColumn = (
  data: ISelectSubjectsData,
  styling: ISelectSubjectsStyling,
  hooks: ISelectSubjectsHooks,
  filterOnChange: (
    filterList: string[][],
    onChange: (val: string | string[], index: number, column: MUIDataTableColumn) => void,
    index: number,
    column: MUIDataTableColumn,
    event: SelectChangeEvent<unknown>
  ) => void
): MUIDataTableColumnDef => {
  const {
    languages: { functionCode: filterCodeLanguages, typeMapping: filterMappingLanguages },
    state,
  } = data;
  const { t, tableDispatch } = hooks;
  const { classes } = styling;

  return {
    name: 'language',
    label: t('exam->>SelectSubjects.LanguageMedium'),
    options: {
      sort: true,
      searchable: true,
      filter: true,
      filterType: 'custom',
      setCellProps: () => ({ style: { width: '10%' } }),
      customFilterListOptions: {
        render: (v: any[]) => {
          if (v.length > 1) {
            return [`${filterMappingLanguages[v[0]].children}, ..`];
          }
          if (v.length === 1) {
            return [filterMappingLanguages[v[0]].children];
          }
          return [];
        },
        update: (filterList: string[][],
          filterPos: number,
          index: number) => {
          // update table filter state
          tableDispatch({
            type: 'UPDATE_FILTERS',
            payload: { ...state.filters, language: [] },
          });

          // update table inner filter
          return customFilterChipRender(filterList, filterPos, index);
        },
      },
      filterOptions: {
        fullWidth: true,
        display: (filterList: string[][], onChange: (val: string | string[], index: number, column: MUIDataTableColumn) => void, index: number, column: MUIDataTableColumn) => (
          <CustomSelect
            className={classes.select}
            displayEmpty
            variant="filled"
            multiple
            // eslint-disable-next-line no-param-reassign,react/destructuring-assignment
            value={filterList[index]}
            menuItems={filterCodeLanguages}
            onChange={(e: SelectChangeEvent<unknown>) => {
              filterOnChange(filterList, onChange, index, column, e);
            }}
            label="Languages"
            labelId="inputSelectLabel-languages"
            id="inputSelect-languages"
            renderValue={(selected: unknown | string[]) =>
              Array.isArray(selected) && selected.length > 0
                ? // @ts-ignore
                filterCodeLanguages
                  .filter((language: any) => selected.includes(language.value))
                  .map((s: any) => s.children)
                  .join(', ')
                : 'Select Languages'
            }
          />
        ),
      },
    },
  };
};

const tableColumns = ({
  data,
  styling,
  hooks,
  handleModal,
}: ITableColumns): MUIDataTableColumnDef[] => {
  const { selectedSubjectsData, subjectListData } = data;
  const { t, mutatePostExamSubjectRegistrationDraft, applicationUuid } = hooks;
  const { classes } = styling;
  const { onChoosePaperModalOpenHandler, onViewDetailOpenHandler } = handleModal;

  const filterOnChange = (
    filterList: string[][],
    onChange: (val: string | string[], index: number, column: MUIDataTableColumn) => void,
    index: number,
    column: MUIDataTableColumn,
    event: SelectChangeEvent<unknown>
  ) => {
    // eslint-disable-next-line no-param-reassign
    filterList[index] = event.target.value as string[];
    onChange(filterList[index], index, column);
    window.dispatchEvent(new CustomEvent('resize'));
  };

  return [
    {
      name: 'subjectName',
      label: t('exam->>SelectSubjects.SubjectName'),
      options: {
        sort: true,
        searchable: true,
        filter: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Box className={classes.subjectContainer}>
            <Typography className={classes.subjectText} variant="body2">
              {subjectListData[dataIndex].subjectName} ({subjectListData[dataIndex].subjectCode})
            </Typography>
            <Button
              className={classes.viewDetailsButton}
              id={`button-view-details-${dataIndex}`}
              variant="text"
              color="primary"
              onClick={() => onViewDetailOpenHandler(subjectListData[dataIndex].subjectCode)}
              size="small"
            >
              View Details
            </Button>
          </Box>
        ),
      },
    },
    {
      name: 'subjectLevel',
      label: t('exam->>SelectSubjects.SubjectLevel'),
      options: {
        sort: true,
        searchable: true,
        filter: false,
        setCellProps: () => ({ style: { width: '10%' } }),
      },
    },
    examSeriesColumn(data, styling, hooks, filterOnChange),
    examLevelColumn(data, styling, hooks),
    languageColumn(data, styling, hooks, filterOnChange),
    {
      name: 'action',
      label: t('exam->>SelectSubjects.Action'),
      options: {
        sort: false,
        searchable: true,
        filter: false,
        setCellProps: () => ({ style: { width: '20%' } }),
        customBodyRenderLite: (dataIndex: number) => {
          const { actionRequired, subjectCode, subjectName, subjectLevel } = subjectListData[
            dataIndex
          ];
          const labelActionRequired: string = actionRequired
            ? t('exam->>SelectSubjects.ChoosePapers')
            : t('exam->>SelectSubjects.AddSubject');
          const isSelected = selectedSubjectsData.find(
            (subject) => subject.subjectCode === subjectCode
          );
          const selectSubjectsAction = () => {
            if (actionRequired) {
              onChoosePaperModalOpenHandler(subjectCode);
            } else {
              mutatePostExamSubjectRegistrationDraft({
                applicationUuid,
                subjectCode,
                examSeriesUuid: '',
                mandatoryPapers: [],
                optionalPapers: [],
                callbackVariable: {
                  subjectName,
                  subjectLevel,
                  subjectCode,
                },
              });
            }
          };
          return (
            <Box>
              {isSelected ? (
                <Box className={classes.successAdded}>
                  <IconWithLabel
                    iconSize="S"
                    iconName={IconName.tick}
                    label={t('exam->>SelectSubjects.Added')}
                    typographyProps={{
                      variant: 'h5',
                    }}
                  />
                </Box>
              ) : (
                <ApiButton
                  className={classes.actionButton}
                  variant="text"
                  color="primary"
                  status="idle"
                  // @ts-ignore
                  onClick={() => selectSubjectsAction(actionRequired)}
                  size="small"
                  disabled={!subjectListData[dataIndex].allowedToAdd}
                  id={`add-subject-${dataIndex}`}
                  startIcon={
                    <Icon
                      name={IconName.add}
                      size="S"
                      color={styling.theme.color.link.linkDefault01}
                    />
                  }
                >
                  {labelActionRequired}
                </ApiButton>
              )}
            </Box>
          );
        },
      },
    },
  ];
};

export const useRawFilters = (applicationUuid: string) => {
  const [examTypeCode, setExamTypeCode] = useState<any>('');

  const examSeries = useAPIDropdown(
    () => candidateService.exam.candidate.useGetExamSeries(),
    true,
    dropDownMappingToSelect
  );

  const languages = useAPIDropdown(
    () => candidateService.exam.candidate.useGetLanguages(),
    true,
    dropDownMappingToSelect
  );

  const examLevels = useAPIDropdown(
    () => candidateService.exam.candidate.usePostExamLevel({ examType: examTypeCode }),
    true,
    dropDownMappingToSelect
  );

  candidateService.exam.candidate.useGetExamRegistration(applicationUuid, {
    onSuccess: (res) => {
      if (res.examType.code) {
        setExamTypeCode(res.examType.code);
      }
    },
    enabled: !!applicationUuid,
  });

  return { examSeries, languages, examLevels, examTypeCode };
};

const useCallMutates = (
  applicationUuid: string,
  tableDispatch: React.Dispatch<TableActionTypes>,
  state: TableState,
  examTypeCode: any
) => {
  const { t } = useTranslation();
  const { snackbarHandler } = useSnackbarHandler();
  const queryClient = useQueryClient();
  const { data: selectedSubjectsData } = useGetSelectedSubjects(applicationUuid);

  const { mutate: mutatePostExamSubjects } = usePostExamSubjects(
    (res: { data: { content: any; totalElements: any } }) => {
      tableDispatch({
        type: 'UPDATE_DATA',
        payload: {
          data: res.data.content,
          count: res.data.totalElements,
        },
      } as TableActionTypes);
    },
    () => {
      // do nothing
    },
    () => {
      // do nothing
    }
  );

  useEffect(
    () => {
      if (examTypeCode) {
        mutatePostExamSubjects({
          searchParam: state.search || '',
          examLevelUuid: getIndexValue(state.filters.examLevel, 0),
          examSeriesUuid: state.filters.examSeries || [],
          languageUuid: state.filters.language || [],
          examType: examTypeCode,
          examRegistrationUuid: applicationUuid,
          apiPathParam: {
            page: state.page,
            pageSize: state.size,
            sort: state.sortColumn,
            order: state.order,
          },
        });
      }
    } /* eslint-disable-next-line react-hooks/exhaustive-deps */,
    [
      state.filters.examLevel,
      state.filters.examSeries,
      state.filters.language,
      state.order,
      state.sortColumn,
      state.page,
      state.size,
      state.sortColumn,
      state.search,
      examTypeCode,
    ]
  );

  const {
    mutate: mutatePostExamSubjectRegistrationDraft,
    status: mutatePostExamSubjectRegistrationDraftStatus,
    reset: mutatePostExamSubjectRegistrationDraftReset,
  } = usePostExamSubjectRegistrationDraft(
    () => {
      queryClient.invalidateQueries(GET_EXAM_REGISTRATION);
      queryClient.invalidateQueries([GET_SELECTED_SUBJECTS, applicationUuid]);
    },
    (err, variable) => {
      if (err?.response?.data.code !== '1602') {
        snackbarHandler?.setGlobalSnackbar({
          targetSeverity: 'error',
          targetData: err?.response?.data.message,
        });
      } else {
        snackbarHandler?.setGlobalSnackbar({
          targetSeverity: 'error',
          targetData: t('exam->>SubjectSnackbar.UnableToAdd', {
            subjectDetails: setSubjectDetails(variable.callbackVariable),
          }),
        });
      }
    },
    () => {
      mutatePostExamSubjectRegistrationDraftReset();
    }
  );

  return {
    selectedSubjectsData,
    mutatePostExamSubjectRegistrationDraft,
    mutatePostExamSubjectRegistrationDraftStatus,
    mutatePostExamSubjectRegistrationDraftReset,
  };
};

interface SelectAddSubjectsProps {
  title: string;
  description: string;
  backNavigation: INavigation;
  toNavigation: INavigation;
  enable?: boolean;
  confirmRouteAndLabel: {
    confirmRoute: string;
    confirmLabel: string;
  };
}

const SelectAddSubjects = (props: SelectAddSubjectsProps) => {
  const { classes } = useStyles();
  const theme = useTheme();
  const { title, description, backNavigation, toNavigation, enable, confirmRouteAndLabel } = props;
  const { t } = useTranslation();
  const { uuid: applicationUuid } = useParams<any>();
  const history = useHistory();
  const { state, dispatch: tableDispatch, tOptions } = useDataTable({
    ...TABLE_INITIAL_STATE,
    sortColumn: 'subjectName',
    order: 'asc',
    size: 10,
    page: 0,
    search: '',
    filters: {},
  });

  const [code, setCode] = useState('');
  const [choosePaperModal, setChoosePaperModal] = useState<Record<string, boolean>>({});
  const [viewDetailModal, setViewDetailModal] = useState(false);
  const { data: subjectDetailData, status: fetchSubjectDetailStatus } = useGetSubjectDetail(
    applicationUuid,
    code
  );

  const { examSeries, languages, examLevels, examTypeCode } = useRawFilters(applicationUuid);
  const {
    selectedSubjectsData,
    mutatePostExamSubjectRegistrationDraft,
    mutatePostExamSubjectRegistrationDraftStatus,
    mutatePostExamSubjectRegistrationDraftReset,
  } = useCallMutates(applicationUuid, tableDispatch, state, examTypeCode);

  const onChoosePaperModalOpenHandler = (subjectCode: string) => {
    setChoosePaperModal((modalState) => ({
      ...modalState,
      [subjectCode]: true,
    }));
    setCode(subjectCode);
  };

  const onChoosePaperModalCloseHandler = (subjectCode: string) => {
    setChoosePaperModal((modalState) => ({
      ...modalState,
      [subjectCode]: false,
    }));
    mutatePostExamSubjectRegistrationDraftReset();
  };

  const onViewDetailOpenHandler = (subjectCode: string) => {
    setViewDetailModal(true);
    setCode(subjectCode);
  };

  const onViewDetailCloseHandler = () => {
    setViewDetailModal(false);
  };

  const mutateChoosePaperSubject = (
    callback: UseMutateFunction<
      AxiosResponse,
      AxiosError<AxiosError<GenericErrorResponse>>,
      ExamSaveSubjectRegistrationDraftRequest
    >,
    mutatePayload: ExamSaveSubjectRegistrationDraftRequest
  ) => {
    callback(mutatePayload);
  };

  return (
    <>
      {subjectDetailData && (
        <ChoosePaperSubjectModal
          applicationUuid={applicationUuid}
          modalData={subjectDetailData}
          onClose={() => onChoosePaperModalCloseHandler(subjectDetailData.subjectCode)}
          modelOpen={choosePaperModal[subjectDetailData.subjectCode]}
          mutateSubjectHandler={{
            mutateSubjectStatus: mutatePostExamSubjectRegistrationDraftStatus,
            mutateSubjectReset: mutatePostExamSubjectRegistrationDraftReset,
          }}
          mutateChoosePaperSubject={(payload: ExamSaveSubjectRegistrationDraftRequest) =>
            mutateChoosePaperSubject(mutatePostExamSubjectRegistrationDraft, payload)
          }
        />
      )}
      <ViewSubjectDetailsModal
        onClose={onViewDetailCloseHandler}
        modelOpen={viewDetailModal}
        subjectDetails={subjectDetailData}
        fetchSubjectDetailStatus={fetchSubjectDetailStatus}
      />
      <Box>
        <ContentTemplate isContinuable type="full">
          <SectionHeader title={(title as unknown) as string} />
          <Typography className={classes.textContainer}>{description}</Typography>
          <Box className={classes.searchContainer}>
            <SearchBar
              className={classes.searchInput}
              placeholder={t('exam->>SelectSubjects.SearchSubjects')}
              name="searchInput"
              ariaLabel="searchInput"
              defaultValue={state.search}
              onSearch={(searchStr: string) => {
                tableDispatch({
                  type: 'UPDATE_SEARCH',
                  payload: searchStr.trim(),
                });
              }}
              clearInputButton
            />
          </Box>

          <Box className={classes.editContainer}>
            {selectedSubjectsData && (
              <MUIDataTable
                // @ts-ignore
                state={state}
                dispatch={tableDispatch}
                columns={tableColumns({
                  data: {
                    subjectListData: state.data,
                    selectedSubjectsData,
                    examSeries,
                    languages,
                    examLevels,
                    state,
                  },
                  styling: {
                    classes,
                    theme,
                  },
                  hooks: {
                    history,
                    t,
                    mutatePostExamSubjectRegistrationDraft,
                    mutatePostExamSubjectRegistrationDraftStatus,
                    applicationUuid,
                    tableDispatch,
                  },
                  handleModal: {
                    onChoosePaperModalOpenHandler,
                    onChoosePaperModalCloseHandler,
                    onViewDetailOpenHandler,
                  },
                })}
                // @ts-ignore
                data={state.data}
                options={{
                  ...tOptions,
                  filter: true,
                  pagination: true,
                  sort: true,
                  selectToolbarPlacement: 'none',
                }}
              />
            )}
          </Box>
        </ContentTemplate>
      </Box>
      {selectedSubjectsData && (
        <>
          <SelectAddSubjectsNavButtons
            backNavigation={backNavigation}
            toNavigation={toNavigation}
            selectedSubjectsData={selectedSubjectsData}
            examTypeCode={examTypeCode}
            enable={enable}
          />
          <SelectedSubjectsDrawer
            confirmRouteAndLabel={confirmRouteAndLabel}
            data={selectedSubjectsData}
          />
        </>
      )}
    </>
  );
};

export default SelectAddSubjects;
